import { useMemo, useState } from "react";
import { PieCustomLayerProps, ResponsivePie } from "@nivo/pie";
import { buildClasses } from "../utils/buildClasses";
import { RyeChartTooltipProvider } from "../rye/RyeChartTooltip";
import { DollarAmount, useFormatDollarAmount } from "../common/DollarAmount";
import {
  BreakdownSelection,
  BreakdownTooltip,
  CHART_TOOLTIP_THEME,
  OTHER_COLOR,
  percentOf,
  shadeFor,
  TYPE_LABELS,
  UnreviewedNote,
  useAvailableChartHeight,
} from "./breakdownChartParts";
import {
  BreakdownSlice,
  BreakdownType,
  combineSmallSlices,
  OTHER_SLICE_ID,
} from "./useCategoryBreakdownData";

const MIN_DONUT_SIZE_PX = 220;
const MAX_DONUT_SIZE_PX = 380;
// Room for a hovered slice to grow outward without being clipped.
const ACTIVE_OFFSET_PX = 6;

interface ColoredSlice extends BreakdownSlice {
  color: string;
}

function CenterTotal({
  centerX,
  centerY,
  total,
  type,
  currency,
}: Pick<PieCustomLayerProps<ColoredSlice>, "centerX" | "centerY"> & {
  total: number;
  type: BreakdownType;
  currency: string;
}): JSX.Element {
  const formatted = useFormatDollarAmount()(total, {
    currencyCode: currency,
    showDollarSign: true,
    showMinus: true,
  });
  return (
    <g style={{ pointerEvents: "none" }}>
      <text
        x={centerX}
        y={centerY - 10}
        textAnchor="middle"
        dominantBaseline="central"
        style={{
          fontFamily: "InterVariable, sans-serif",
          fontSize: 12,
          fill: "#64748b", // on-surface-500
        }}
      >
        {TYPE_LABELS[type]}
      </text>
      <text
        x={centerX}
        y={centerY + 12}
        textAnchor="middle"
        dominantBaseline="central"
        style={{
          fontFamily: "InterVariable, sans-serif",
          fontSize: 20,
          fontWeight: 500,
          fill: "#334155", // on-surface-700
        }}
      >
        {formatted}
      </text>
    </g>
  );
}

/**
 * A donut with a ranked list beside it, which carries the names and amounts.
 * Past a handful of slices, the smallest are combined into "Other".
 */
export function BreakdownDonut({
  slices,
  type,
  currency,
  unreviewedCount,
  selectedId,
  onSelect,
}: {
  slices: BreakdownSlice[];
  type: BreakdownType;
  currency: string;
  unreviewedCount: number;
  selectedId: string | null;
  onSelect: (selection: BreakdownSelection) => void;
}): JSX.Element {
  const availableHeightPx = useAvailableChartHeight();
  const donutSizePx = Math.min(
    MAX_DONUT_SIZE_PX,
    Math.max(MIN_DONUT_SIZE_PX, availableHeightPx),
  );

  const coloredSlices = useMemo<ColoredSlice[]>(
    () =>
      combineSmallSlices(slices).map((slice, rank) => ({
        ...slice,
        color: slice.id === OTHER_SLICE_ID ? OTHER_COLOR : shadeFor(type, rank),
      })),
    [slices, type],
  );
  const total = coloredSlices.reduce((sum, slice) => sum + slice.value, 0);

  function select(slice: BreakdownSlice) {
    onSelect({
      id: slice.id,
      label:
        slice.id === OTHER_SLICE_ID
          ? `Other ${TYPE_LABELS[type].toLowerCase()}`
          : slice.label,
      transactions: slice.transactions,
    });
  }

  // Hovering a slice or its row in the list highlights both.
  const [activeId, setActiveId] = useState<string | null>(null);

  return (
    <div
      className={buildClasses(
        "flex",
        "items-start",
        "justify-center",
        "gap-10",
        "flex-wrap",
      )}
    >
      <RyeChartTooltipProvider
        className={buildClasses("shrink-0", "[&_path]:cursor-pointer")}
        style={{ width: `${donutSizePx}px`, height: `${donutSizePx}px` }}
      >
        <ResponsivePie<ColoredSlice>
          data={coloredSlices}
          id="id"
          value="value"
          colors={(slice) => slice.data.color}
          margin={{
            top: ACTIVE_OFFSET_PX,
            right: ACTIVE_OFFSET_PX,
            bottom: ACTIVE_OFFSET_PX,
            left: ACTIVE_OFFSET_PX,
          }}
          innerRadius={0.62}
          padAngle={0.8}
          cornerRadius={3}
          sortByValue={false}
          activeOuterRadiusOffset={ACTIVE_OFFSET_PX}
          activeId={activeId}
          onActiveIdChange={(id) =>
            setActiveId(id === null ? null : String(id))
          }
          borderWidth={0}
          enableArcLabels={false}
          enableArcLinkLabels={false}
          theme={CHART_TOOLTIP_THEME}
          onClick={(slice) => select(slice.data)}
          tooltip={({ datum }) => (
            <BreakdownTooltip
              color={datum.data.color}
              label={datum.data.label}
              value={datum.data.value}
              total={total}
              currency={currency}
            />
          )}
          layers={[
            "arcs",
            (props) => (
              <CenterTotal
                centerX={props.centerX}
                centerY={props.centerY}
                total={total}
                type={type}
                currency={currency}
              />
            ),
          ]}
          role="img"
        />
      </RyeChartTooltipProvider>
      <div
        className={buildClasses(
          "flex",
          "flex-col",
          "gap-0.5",
          "min-w-[16rem]",
          "max-w-md",
          "flex-1",
        )}
      >
        {coloredSlices.map((slice) => (
          <button
            key={slice.id}
            type="button"
            onMouseEnter={() => setActiveId(slice.id)}
            onMouseLeave={() => setActiveId(null)}
            onFocus={() => setActiveId(slice.id)}
            onBlur={() => setActiveId(null)}
            onClick={() => select(slice)}
            aria-label={`Show ${slice.label} transactions`}
            aria-pressed={slice.id === selectedId}
            className={buildClasses(
              "grid",
              "grid-cols-[auto_1fr_auto_3rem]",
              "items-center",
              "gap-3",
              "px-2",
              "py-1.5",
              "rounded-md",
              "text-sm",
              "text-left",
              "text-on-surface-700",
              "outline-none",
              "focus-visible:outline",
              "focus-visible:outline-2",
              "focus-visible:outline-purple-500",
              {
                if: slice.id === selectedId,
                then: "bg-on-surface-200",
                elseIfs: [
                  { elseIf: slice.id === activeId, then: "bg-on-surface-100" },
                ],
              },
            )}
          >
            <span
              className={buildClasses("w-2.5", "h-2.5", "rounded-sm")}
              style={{ backgroundColor: slice.color }}
            />
            <span className={buildClasses("truncate")}>{slice.label}</span>
            <DollarAmount
              n={slice.value}
              size="sm"
              showDollarSign={true}
              showMinus={false}
              showPlus={false}
              align="right"
              currencyCode={currency}
            />
            <span className={buildClasses("text-right", "text-on-surface-500")}>
              {percentOf(slice.value, total)}
            </span>
          </button>
        ))}
        <UnreviewedNote
          count={unreviewedCount}
          className={buildClasses("px-2", "pt-3")}
        />
      </div>
    </div>
  );
}
